import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import SimulatedBanner from "@/components/SimulatedBanner";
import { ChevronLeft, Loader2, Send, Eye, Mail, MailOpen } from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

const STATUS_LABELS = {
  draft: "Borrador",
  scheduled: "Programada",
  sending: "Enviando",
  sent: "Enviada",
  failed: "Fallida",
};

const STATUS_STYLES = {
  draft: "bg-stone-100 text-stone-700 border-stone-200",
  scheduled: "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40",
  sending: "bg-blue-50 text-blue-700 border-blue-200",
  sent: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
};

export default function EmailCampaignDetail() {
  const { campaignId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {send, html}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/email/campaigns/${campaignId}/sends`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendNow = async () => {
    try {
      await api.post(`/email/campaigns/${campaignId}/send`);
      toast.success("Campaña enviada (simulada)");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const openPreview = (send) => {
    // El HTML del correo + el pixel de tracking real (dispara la apertura al renderizar).
    // El body_html es contenido de usuario/plantilla: se sanea con DOMPurify antes
    // de renderizarlo con dangerouslySetInnerHTML para prevenir XSS.
    const pixelUrl = `${API_BASE}/email/track/open/${send.id}.png`;
    const rawHtml = `${data.campaign.body_html}<img src="${pixelUrl}" width="1" height="1" style="display:block;opacity:0" alt="">`;
    const html = DOMPurify.sanitize(rawHtml, {
      USE_PROFILES: { html: true },
      ADD_ATTR: ['target', 'rel'],
    });
    setPreview({ send, html });
    // Recargar métricas tras 1.2s para reflejar el open
    setTimeout(() => load(), 1200);
  };

  if (loading || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-emerald" />
      </div>
    );
  }

  const { campaign, sends, metrics } = data;

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="campaign-detail-page">
      <div>
        <Link to="/email-marketing" className="text-xs text-brand-emerald-dark hover:underline flex items-center gap-1 mb-3">
          <ChevronLeft className="h-3.5 w-3.5" /> Volver a Email Marketing
        </Link>
        <div className="divider-gold mb-3" />
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
              <Mail className="h-8 w-8 text-brand-emerald" />
              {campaign.name}
              <SimulatedBanner compact />
            </h1>
            <p className="text-sm text-stone-500 mt-2">
              <strong>{campaign.subject}</strong> · {campaign.account_email} → {campaign.list_name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`border font-semibold ${STATUS_STYLES[campaign.status] || ""}`} data-testid="campaign-status">
              {STATUS_LABELS[campaign.status] || campaign.status}
            </Badge>
            {campaign.status !== "sent" && campaign.status !== "sending" && (
              <Button onClick={sendNow} data-testid="campaign-detail-send" className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold">
                <Send className="h-4 w-4 mr-2" /> Enviar ahora
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="card-elevated p-6" data-testid="metric-sent">
          <div className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold">Enviados</div>
          <div className="mt-2 font-serif-display text-4xl text-brand-ink font-semibold">{metrics.sent}</div>
        </div>
        <div className="card-elevated p-6" data-testid="metric-opened">
          <div className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold">Abiertos</div>
          <div className="mt-2 font-serif-display text-4xl text-brand-emerald-dark font-semibold">{metrics.opened}</div>
        </div>
        <div className="card-elevated p-6" data-testid="metric-open-rate">
          <div className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold">Tasa de apertura</div>
          <div className="mt-2 font-serif-display text-4xl text-brand-ink font-semibold">
            {metrics.open_rate.toFixed(1)}<span className="text-2xl">%</span>
          </div>
        </div>
      </div>

      {/* Sends table */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif-display text-2xl text-brand-ink">Destinatarios</h2>
          <span className="text-xs text-stone-500">
            Ver el correo dispara el pixel real de tracking · Modo simulado
          </span>
        </div>
        {sends.length === 0 ? (
          <div className="card-elevated p-12 text-center text-stone-500">
            Aún no se ha enviado. Use &quot;Enviar ahora&quot;.
          </div>
        ) : (
          <div className="card-elevated overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/60">
                  <TableHead>Contacto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Enviado</TableHead>
                  <TableHead>Abierto</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sends.map((s) => (
                  <TableRow key={s.id} data-testid={`send-row-${s.contact_email}`}>
                    <TableCell>
                      <div className="font-medium text-brand-ink">{s.contact_name}</div>
                      <div className="text-xs text-stone-500">{s.contact_email}</div>
                    </TableCell>
                    <TableCell>
                      {s.opened_at ? (
                        <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border border-brand-emerald/30 font-semibold">
                          <MailOpen className="h-3 w-3 mr-1" /> Abierto
                        </Badge>
                      ) : (
                        <Badge className="bg-stone-100 text-stone-700 border border-stone-200 font-semibold">
                          <Mail className="h-3 w-3 mr-1" /> Enviado
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-stone-500">{fmt(s.sent_at)}</TableCell>
                    <TableCell className="text-xs text-stone-500">
                      {s.opened_at ? (
                        <span>{fmt(s.opened_at)} <span className="text-brand-emerald-dark font-semibold">· {s.open_count}×</span></span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openPreview(s)} data-testid={`send-preview-${s.contact_email}`} className="text-brand-emerald-dark">
                        <Eye className="h-4 w-4 mr-1" /> Ver correo
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              Vista previa del correo
            </DialogTitle>
            <DialogDescription>
              Simulación de bandeja de entrada — al abrir se dispara el pixel real de tracking.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-stone-50 px-4 py-3 border-b border-border text-sm">
                <div><span className="text-stone-500">Para:</span> <strong>{preview.send.contact_email}</strong></div>
                <div><span className="text-stone-500">Asunto:</span> <strong>{campaign.subject}</strong></div>
                <div><span className="text-stone-500">De:</span> {campaign.account_email}</div>
              </div>
              <div
                className="p-6 bg-white max-h-[50vh] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: preview.html }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
